import { useEffect, useState } from "react";
import { ReceiptText, MessageCircle, Smartphone, Download, Copy, Check } from "lucide-react";
import { api, money, errorMessage } from "../api";

function fmtDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata" });
  } catch { return iso; }
}

export default function BillView({ branch, outlet, lastBill, setLastBill }) {
  const [history, setHistory] = useState([]);
  const [selected, setSelected] = useState(lastBill || null);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/sales?branch=${branch}`);
      setHistory(data);
      if (!selected && data.length) setSelected(data[data.length - 1]);
    } catch (e) { console.error(errorMessage(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [branch, lastBill]);

  const bill = selected || lastBill;
  const o = bill?.outlet || outlet || {};

  const invoiceUrl = bill ? `${window.location.origin}/invoice/${bill.id}` : "";

  const buildText = () => {
    if (!bill) return "";
    const name = bill.customer_name ? ` ${bill.customer_name}` : "";
    const brand = o.display_name || "FynDine Restaurant";
    return [
      `Namaste${name}! 🙏`,
      ``,
      `Your tax invoice from *${brand}* is ready.`,
      `Invoice: ${bill.invoice_no}`,
      `Amount: ₹${bill.grand_total}`,
      ``,
      `Tap to view the full GST invoice:`,
      invoiceUrl,
      ``,
      `Thank you for dining with us!`,
    ].join("\n");
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(buildText()); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* noop */ }
  };

  const normalizePhone = (raw) => {
    if (!raw) return "";
    const digits = String(raw).replace(/\D/g, "");
    if (!digits) return "";
    // Already in international format with country code (12-15 digits)
    if (digits.length >= 11 && digits.length <= 15) {
      // If starts with 0 and length 11, likely trunk prefix -> treat remaining 10 as Indian local
      if (digits.length === 11 && digits.startsWith("0")) return "91" + digits.slice(1);
      return digits;
    }
    // Plain 10-digit Indian mobile -> prepend India country code 91
    if (digits.length === 10) return "91" + digits;
    // Leading 0 + 10-digit Indian mobile
    if (digits.length === 11 && digits.startsWith("0")) return "91" + digits.slice(1);
    return digits;
  };

  const phone = normalizePhone(bill?.customer_phone);
  const smsPhone = bill?.customer_phone ? (phone.startsWith("91") ? "+" + phone : phone) : "";
  const text = encodeURIComponent(buildText());

  return (
    <section className="bill-layout" data-testid="bill-view">
      <div className="bill-form panel">
        <div className="panel-head">
          <div><span className="overline">CUSTOMER COMMUNICATION</span><h2>GST-compliant bill</h2></div>
          <ReceiptText size={21} color="#e67e32" />
        </div>

        {history.length > 0 && (
          <label>Select invoice
            <select value={bill?.id || ""} onChange={(e) => setSelected(history.find((h) => h.id === e.target.value))} data-testid="bill-select">
              {history.slice().reverse().map((h) => (
                <option key={h.id} value={h.id}>{`${h.invoice_no} · ${money(h.grand_total)} · ${fmtDate(h.created_at)}`}</option>
              ))}
            </select>
          </label>
        )}

        {!bill && <div className="empty">No invoices yet. Create one from POS &amp; Sales.</div>}

        {bill && (
          <>
            <label>Customer mobile<input value={bill.customer_phone || ""} readOnly data-testid="bill-phone"/></label>
            <label>Shareable invoice link<input value={invoiceUrl} readOnly data-testid="bill-link"/></label>
            <p className="form-hint">The link opens a hosted GST-compliant invoice page the customer can view on any device and save as PDF. WhatsApp &amp; SMS send a short message with this link.</p>
            <div className="share-actions">
              <a className={`share-button whatsapp ${!phone ? "disabled" : ""}`} href={phone ? `https://wa.me/${phone}?text=${text}` : "#"} target="_blank" rel="noreferrer" data-testid="share-bill-whatsapp"><MessageCircle size={15}/> Send on WhatsApp</a>
              <a className={`share-button sms ${!phone ? "disabled" : ""}`} href={phone ? `sms:${smsPhone}?body=${text}` : "#"} data-testid="share-bill-sms"><Smartphone size={15}/> Send SMS link</a>
              <a className="share-button copy" href={invoiceUrl} target="_blank" rel="noreferrer" data-testid="open-bill-link"><Copy size={15}/> Open invoice</a>
              <button className="share-button copy" onClick={copy} data-testid="share-bill-copy">{copied ? <Check size={15}/> : <Copy size={15}/>} {copied ? "Copied" : "Copy message"}</button>
              <button className="share-button print" onClick={() => window.print()} data-testid="share-bill-print"><Download size={15}/> Print / Save PDF</button>
            </div>
          </>
        )}
      </div>

      <div className="bill-preview panel">
        <span className="overline">INVOICE PREVIEW</span>
        {bill ? (
          <div className="receipt gst-receipt" id="print-area">
            <div className="receipt-brand">
              <b>{o.display_name || "FynDine Restaurant"}</b>
              {o.legal_name && <small>{o.legal_name}</small>}
              {(o.address || o.city) && <small>{o.address}{o.city ? ", " + o.city : ""}{o.pincode ? " · " + o.pincode : ""}</small>}
              {o.contact_phone && <small>Ph: {o.contact_phone}{o.contact_email ? " · " + o.contact_email : ""}</small>}
              <small>{o.gstin ? `GSTIN: ${o.gstin}` : ""}{o.gstin && o.fssai ? "  ·  " : ""}{o.fssai ? `FSSAI: ${o.fssai}` : ""}</small>
            </div>

            <div className="receipt-meta">
              <div><small>Invoice</small><b>{bill.invoice_no}</b></div>
              <div><small>Date</small><b>{fmtDate(bill.created_at)}</b></div>
              <div><small>Channel</small><b>{bill.channel}</b></div>
              {bill.table_label && <div><small>Table</small><b>{bill.table_label}</b></div>}
              {bill.waiter_name && <div><small>Served by</small><b>{bill.waiter_name}</b></div>}
              {bill.customer_name && <div><small>Customer</small><b>{bill.customer_name}</b></div>}
              {(bill.kot_numbers || []).length > 0 && <div style={{gridColumn:"1 / -1"}}><small>KOT reference</small><b>{bill.kot_numbers.join(", ")}</b></div>}
            </div>

            <table className="receipt-table">
              <thead><tr><th>Item</th><th>HSN</th><th>Qty</th><th>Rate</th><th>GST%</th><th>Total</th></tr></thead>
              <tbody>
                {bill.items.map((it, idx) => (
                  <tr key={idx}>
                    <td>{it.name}</td>
                    <td>{it.hsn_sac || "—"}</td>
                    <td>{it.qty}</td>
                    <td>₹{it.unit_price}</td>
                    <td>{it.gst_rate}%</td>
                    <td>₹{(it.line_amount + it.tax_amount).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="receipt-totals">
              <div><span>Subtotal</span><b>{money(bill.subtotal)}</b></div>
              {bill.interstate ? (
                <div><span>IGST</span><b>{money(bill.igst)}</b></div>
              ) : (
                <>
                  <div><span>CGST</span><b>{money(bill.cgst)}</b></div>
                  <div><span>SGST</span><b>{money(bill.sgst)}</b></div>
                </>
              )}
              {bill.service_charge > 0 && <div><span>Service charge</span><b>{money(bill.service_charge)}</b></div>}
              {bill.discount > 0 && <div><span>Discount</span><b>-{money(bill.discount)}</b></div>}
              <div className="grand-row"><span>Grand total</span><strong>{money(bill.grand_total)}</strong></div>
            </div>

            <div className="receipt-foot">
              <small>GST on reverse charge: No. Supply type: {bill.interstate ? "Interstate" : "Intrastate"}.</small>
              <small>Thank you for dining with us. All disputes subject to {o.city || "local"} jurisdiction.</small>
            </div>
          </div>
        ) : (
          <div className="empty">Create a sale from POS to see the GST bill here.</div>
        )}
      </div>
    </section>
  );
}
